import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { ChevronDown, ArrowRight } from "lucide-react";

const FAQS = [
  {
    q: "What is Regulens?",
    a: (
      <>
        Regulens is a compliance intelligence tool built specifically for Shopify merchants.
        It analyzes your store&rsquo;s profile against publicly available sales tax economic
        nexus rules for 50 U.S. jurisdictions (48 states + DC) and consumer privacy laws with active numeric scoring for 15 states, producing an
        informational Readiness Score with actionable recommendations.
      </>
    ),
  },
  {
    q: "Is this legal advice?",
    a: (
      <>
        No. Regulens provides general informational content only. Nothing produced by
        Regulens constitutes legal advice, tax advice, or any professional advisory service.
        Always consult a licensed attorney and qualified tax professional before making any
        business, legal, or financial decisions.
      </>
    ),
  },
  {
    q: "What does my Readiness Score mean?",
    a: (
      <>
        Your score is a 0&ndash;100 indicator of how well your store profile aligns with
        publicly available regulatory reference points across your selected jurisdictions.
        Scores are graded A through F.
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>A (90&ndash;100)</strong>: Strong Readiness.</li>
          <li><strong>B (75&ndash;89)</strong>: Good Readiness.</li>
          <li><strong>C (60&ndash;74)</strong>: Moderate Readiness.</li>
          <li><strong>D (40&ndash;59)</strong>: Low Readiness.</li>
          <li><strong>F (0&ndash;39)</strong>: Significant Gaps.</li>
        </ul>
        A higher score means stronger alignment with publicly available regulatory benchmarks.
        It does not mean you are legally compliant.
      </>
    ),
  },
  {
    q: "What states does Regulens cover?",
    a: (
      <>
        Regulens Version 4.0 covers sales tax economic nexus rules for 50 U.S. jurisdictions (48 states + DC) and consumer privacy laws with active numeric scoring for 15 states. Not all U.S. states are covered for privacy scoring in the current edition — additional privacy laws exist in the database but are not yet active for numeric scoring.
        Visit our <Link to="/data-verification" className="text-[var(--rg-sage)] hover:underline">Data Verification</Link> page
        to see the full list with source citations and verification dates.
      </>
    ),
  },
  {
    q: "What is economic nexus?",
    a: (
      <>
        Economic nexus means a state requires you to collect and remit sales tax once your
        sales into that state exceed a certain threshold &mdash; even if you have no physical
        presence there. Most states set this at $100,000 in annual revenue. Some states like
        New York also require a minimum number of transactions. After the 2018 South Dakota v.
        Wayfair Supreme Court decision, all U.S. states with sales taxes adopted economic
        nexus rules.
      </>
    ),
  },
  {
    q: "Why is my score low?",
    a: (
      <>
        Common reasons for a low score include: selling into states where you have not
        registered for sales tax, missing a privacy policy on your store, not having an
        opt-out mechanism for data sales, or not having documented your jurisdiction
        exposure. Your report&rsquo;s remediation plan shows the highest-impact actions you
        can take to improve your score.
      </>
    ),
  },
  {
    q: "How often should I re-audit?",
    a: (
      <>
        We recommend running a fresh audit whenever: your revenue grows significantly, you
        start selling in new states, a state law changes, or quarterly as a general practice.
        Most merchants benefit from a fresh audit every 1&ndash;3 months. Your plan includes
        a set number of fresh AI audits per month.
      </>
    ),
  },
  {
    q: "How is my data kept private?",
    a: (
      <>
        Your store URL and revenue information are used only to generate your readiness
        report. We do not sell your data to third parties. For full details see our{" "}
        <Link to="/privacy-policy" className="text-[var(--rg-sage)] hover:underline">Privacy Policy</Link>.
      </>
    ),
  },
  {
    q: "What is the difference between a fresh audit and a cached result?",
    a: (
      <>
        A fresh audit runs a new AI analysis of your store profile against our current
        database. A cached result shows your previous audit result instantly at no API cost.
        Your score is re-cached automatically whenever our database is updated with new
        regulatory data.
      </>
    ),
  },
  {
    q: "Does Regulens cover federal laws?",
    a: (
      <>
        No. Regulens covers state-level sales tax economic nexus and state-level consumer
        privacy laws only. Federal regulations including COPPA, FTC rules, ADA, CAN-SPAM,
        GDPR, and industry-specific regulations are explicitly outside Regulens scope. See
        our <Link to="/federal-scope-notice" className="text-[var(--rg-sage)] hover:underline">Federal Scope Notice</Link> for
        full details.
      </>
    ),
  },
  {
    q: "What happens after I run an audit?",
    a: (
      <>
        Your full Readiness Report appears showing your composite score, all 4 pillar scores,
        per-jurisdiction analysis, and a remediation plan sorted by priority. Each
        recommendation links to the official government source. You can export the report as
        PDF (Growth tier and above).
      </>
    ),
  },
  {
    q: "How do I cancel my subscription?",
    a: (
      <>
        You can cancel anytime from your account settings. On cancellation your account
        downgrades to Free tier at the end of your current billing period. You keep access
        to your last audit result on the Free tier indefinitely.
      </>
    ),
  },
];

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="faq-page">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Support</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-[var(--rg-text-strong)] mb-4">Frequently Asked Questions</h1>
          <p className="text-[var(--rg-text)] text-base">Everything you need to know about how Regulens works.</p>
        </div>

        <div className="space-y-2" data-testid="faq-accordion">
          {FAQS.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={item.q}
                className={`rounded-md border ${open ? "border-[var(--rg-sage)] bg-[var(--rg-sage)]/5" : "border-[var(--rg-border)] bg-[var(--rg-surface)]"} transition-fast`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-start justify-between gap-4 text-left px-5 py-4 hover:bg-[var(--rg-surface-2)]/40"
                  data-testid={`faq-q-${i}`}
                >
                  <span className="font-heading font-semibold text-[var(--rg-text-strong)]">Q{i + 1}: {item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[var(--rg-muted)] mt-1 flex-shrink-0 transition-transform ${open ? "rotate-180 text-[var(--rg-sage)]" : ""}`} />
                </button>
                {open && (
                  <div className="px-5 pb-5 text-sm text-[var(--rg-text)] leading-relaxed" data-testid={`faq-a-${i}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 text-center" data-testid="faq-cta">
          <div className="text-[var(--rg-text-strong)] font-heading font-bold text-lg">Still have questions?</div>
          <Link to="/contact" className="inline-flex items-center gap-1 text-[var(--rg-sage)] hover:underline mt-1 text-sm" data-testid="faq-contact-link">
            Contact Support <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10"><Disclaimer /></div>
      </section>
    </Layout>
  );
}
